/* ============================================================
   Consistency over a habit's intended cadence — the calm replacement for a
   consecutive-calendar-day streak (CLAUDE.md: "show consistency, not a fragile
   streak"; docs/habit-vs-recurring-task.md "Model implications").

   Two things make this schedule-aware rather than a day counter:

   1. **Unscheduled days are neutral.** A Tuesday off-day on a Mon/Wed/Fri habit
      neither counts nor breaks anything. The old day-streak treated it as a miss.
   2. **The unit follows the cadence.** `daily`/`weekdays` are measured in
      scheduled DAYS; `timesPerWeek` is measured in WEEKS, because "which days"
      is explicitly the user's choice and a day-run would be meaningless.

   The live period is always excluded from the trend and reported separately —
   the same grace the old streak had ("not broken until the day passes"), now
   applied to weeks too. So a Monday morning never reads as 0% for the week.

   Repetition is counted in DISTINCT DAYS, never in reps. `completedDays` is a
   set of days, so three sessions crammed into one Monday is one active day —
   which is the whole point of a habit (see docs/habit-schedules.md).
   ============================================================ */
import { addDaysYMD } from '../recurrence/dates';
import { daysBetween, isDueOn, weekBounds } from './schedule';
import { CONSISTENCY_WINDOW_DAYS, type HabitSchedule } from './types';

/** Progress through the period in flight — "2 of 3 this week". */
export interface PeriodProgress {
  done: number;
  target: number;
}

/** Everything about a habit that is derived from its cadence + check-in history. */
export interface CadenceState {
  /** should the habit surface today (false on a planned rest day) */
  dueToday: boolean;
  /** the live week's progress, or null for `daily` (the day IS the period) */
  period: PeriodProgress | null;
  /** kept ÷ expected over completed periods in the window, 0..1.
      null when there is nothing to measure yet — a fresh habit shows no rate
      rather than a discouraging 0% or a fake 100%. */
  rate: number | null;
  kept: number;
  expected: number;
  /** consecutive satisfied periods, in `unit` */
  streak: number;
  /** longest run of satisfied periods ever, in `unit` */
  best: number;
  /** what `streak`/`best` count — so the UI can label them honestly */
  unit: 'day' | 'week';
  /** a quiet "today is the last chance to keep this period" signal. Never a
      loss-aversion prompt (CLAUDE.md tone) — it stays false once a period is
      already out of reach, because nagging about an unreachable week is
      exactly the shaming this app rejects. */
  atRisk: boolean;
}

export interface CadenceInput {
  schedule: HabitSchedule;
  /** the day the habit began — days before it are not misses */
  startsOn: string;
  /** every day the habit was completed, "YYYY-MM-DD" (order/duplicates fine) */
  completedDays: readonly string[];
  /** the client's local today */
  today: string;
  /** `getDay()` index the user's week begins on (0 = Sunday) */
  weekStartsOn: number;
}

/** Derive a habit's cadence state. Pure; `today` is always supplied. */
export function deriveCadenceState(input: CadenceInput): CadenceState {
  return input.schedule.kind === 'timesPerWeek' ? weeklyQuota(input) : perDay(input);
}

/* ---------- day-unit cadences: `daily` and `weekdays` ---------- */

function perDay(input: CadenceInput): CadenceState {
  const { schedule, startsOn, today, completedDays } = input;
  const done = new Set(completedDays);
  const due = (day: string) => isDueOn(schedule, day, startsOn);

  // Trend over the trailing window, EXCLUDING today (today isn't a miss yet).
  const from = maxDay(startsOn, addDaysYMD(today, -CONSISTENCY_WINDOW_DAYS));
  const windowDays = daysBetween(from, addDaysYMD(today, -1)).filter(due);
  const expected = windowDays.length;
  const kept = windowDays.filter((d) => done.has(d)).length;

  // Current run: start at today if it's kept, else at the previous scheduled day
  // — so an unfinished today never reads as a break.
  let cursor = due(today) && done.has(today) ? today : prevDue(today, due, startsOn);
  let streak = 0;
  while (cursor !== null && done.has(cursor)) {
    streak++;
    cursor = prevDue(cursor, due, startsOn);
  }

  // Best: longest run of consecutive SCHEDULED days that were kept.
  let best = 0;
  let run = 0;
  for (const day of daysBetween(startsOn, today)) {
    if (!due(day)) continue; // unscheduled days don't extend or break a run
    run = done.has(day) ? run + 1 : 0;
    if (run > best) best = run;
  }

  const dueToday = due(today);
  return {
    dueToday,
    period: schedule.kind === 'weekdays' ? weekdayPeriod(input, done) : null,
    rate: expected === 0 ? null : kept / expected,
    kept,
    expected,
    streak,
    best,
    unit: 'day',
    atRisk: dueToday && !done.has(today) && streak > 0,
  };
}

/** The nearest scheduled day strictly before `day`, or null once past `startsOn`. */
function prevDue(day: string, due: (d: string) => boolean, startsOn: string): string | null {
  let cursor = addDaysYMD(day, -1);
  // A week has 7 days, so at most 7 steps reach the previous scheduled one.
  for (let i = 0; i < 7; i++) {
    if (cursor < startsOn) return null;
    if (due(cursor)) return cursor;
    cursor = addDaysYMD(cursor, -1);
  }
  return null;
}

/** "2 of 3 this week" for a fixed-weekday habit — how many of this week's
    scheduled days are kept so far. */
function weekdayPeriod(input: CadenceInput, done: Set<string>): PeriodProgress | null {
  const { schedule, startsOn, today, weekStartsOn } = input;
  const { start, end } = weekBounds(today, weekStartsOn);
  const scheduled = daysBetween(maxDay(start, startsOn), end).filter((d) =>
    isDueOn(schedule, d, startsOn),
  );
  if (scheduled.length === 0) return null;
  return { done: scheduled.filter((d) => done.has(d)).length, target: scheduled.length };
}

/* ---------- week-unit cadence: `timesPerWeek` ---------- */

function weeklyQuota(input: CadenceInput): CadenceState {
  const { schedule, startsOn, today, completedDays, weekStartsOn } = input;
  if (schedule.kind !== 'timesPerWeek') throw new Error('weeklyQuota: wrong schedule kind');
  const times = schedule.times;
  const done = new Set(completedDays);

  const current = weekBounds(today, weekStartsOn);
  const keptIn = (bounds: { start: string; end: string }) =>
    daysBetween(maxDay(bounds.start, startsOn), bounds.end).filter((d) => done.has(d)).length;

  const doneThisWeek = keptIn(current);

  // Trend over whole weeks BEFORE the current one. A week that only partly
  // overlaps the habit's life is skipped rather than pro-rated — a habit created
  // on a Friday shouldn't start life at 1/3 for a week it never had.
  const weeks = CONSISTENCY_WINDOW_DAYS / 7;
  let kept = 0;
  let expected = 0;
  for (let i = 1; i <= weeks; i++) {
    const bounds = weekBounds(addDaysYMD(current.start, -7 * i), weekStartsOn);
    if (bounds.start < startsOn) continue;
    expected += times;
    kept += Math.min(keptIn(bounds), times);
  }

  // Current run of satisfied weeks. The live week counts only once it's already
  // satisfied; an unfinished week never breaks the run.
  let streak = 0;
  let cursor = doneThisWeek >= times ? current : weekBounds(addDaysYMD(current.start, -1), weekStartsOn);
  while (cursor.start >= startsOn && keptIn(cursor) >= times) {
    streak++;
    cursor = weekBounds(addDaysYMD(cursor.start, -1), weekStartsOn);
  }

  // Best: longest run of satisfied whole weeks across the habit's life.
  let best = 0;
  let run = 0;
  let scan = weekBounds(startsOn, weekStartsOn);
  if (scan.start < startsOn) scan = weekBounds(addDaysYMD(scan.start, 7), weekStartsOn);
  while (scan.start <= current.start) {
    run = keptIn(scan) >= times ? run + 1 : 0;
    if (run > best) best = run;
    scan = weekBounds(addDaysYMD(scan.start, 7), weekStartsOn);
  }

  // Last-chance: exactly as many days left as reps still needed.
  const daysLeft = daysBetween(today, current.end).length;
  const need = times - doneThisWeek;

  return {
    dueToday: today >= startsOn,
    period: { done: doneThisWeek, target: times },
    rate: expected === 0 ? null : kept / expected,
    kept,
    expected,
    streak,
    best,
    unit: 'week',
    atRisk: need > 0 && daysLeft === need,
  };
}

/** The later of two "YYYY-MM-DD" days (lexicographic compare is exact). */
function maxDay(a: string, b: string): string {
  return a >= b ? a : b;
}
